import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/** A stored file as every feature refers to it. */
export interface FileRef {
  id: string;
  name: string;
  size: number;
  mime: string;
  status?: string;
  uploaded_at?: string | null;
  /** The record lists a file that is gone for good; saving the record drops it. */
  missing?: boolean;
}

/** What the server allows this user to upload in one module, and how the browser should prepare files. */
export interface UploadLimits {
  can_upload: boolean;
  reason: string | null;
  max_file_bytes: number;
  allowed_extensions: string[];
  max_files_per_record: number;
  quota_bytes: number | null;
  usage: { used_bytes: number; reserved_bytes: number; file_count: number };
  image_compression: { enabled: boolean; max_dimension_px: number; jpeg_quality: number; min_bytes: number };
  dedup: { enabled: boolean; max_hash_mb: number };
  direct_upload: boolean;
}

export type UploadModule = 'it_proof' | 'reimbursement' | 'flexi_proof' | 'loan_document' | 'form';

/** A removed file that can still come back (Recently removed, the recycle bin). */
export interface RemovedFile {
  id: string;
  name: string;
  size: number;
  mime: string;
  module: string;
  ref_table: string | null;
  ref_id: string | null;
  ref_field: string | null;
  reason: 'REMOVED' | 'RECORD_DELETED' | 'RECORD_PURGED' | 'ORPHAN' | null;
  deleted_at: string | null;
  deleted_by: string | null;
  purge_after: string | null;
  /** False when the record it was on is gone: it can still be downloaded until its period ends. */
  can_restore: boolean;
}

/** Put a removed file back on the record it came from, under that record's rules. */
export function restoreFile(id: string) {
  return api.post<FileRef>(`/files/${id}/restore`);
}

/** After a delete or a restore anywhere, so every "Recently removed" list on screen shows it. */
export function refreshRecentlyRemoved(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: ['files', 'recently-removed'] });
}

/** Removed files of one record, or — with `parent` — of every proof under it (the proofs of one declaration). */
export function useRecentlyRemoved(refTable: string, by: { refId?: string | null; parent?: string | null }) {
  const params = by.parent ? { ref_table: refTable, parent: by.parent } : { ref_table: refTable, ref_id: by.refId ?? '' };
  return useQuery({
    queryKey: ['files', 'recently-removed', refTable, by.refId ?? null, by.parent ?? null],
    queryFn: () => api.get<RemovedFile[]>('/files/recently-removed', params),
    enabled: !!(by.refId || by.parent),
  });
}

export function useUploadLimits(module: UploadModule, enabled = true) {
  return useQuery({
    queryKey: ['files', 'limits', module],
    queryFn: () => api.get<UploadLimits>('/files/limits', { module }),
    staleTime: 60_000,
    enabled,
  });
}
